import AddIcon from "@mui/icons-material/Add";
import EmailIcon from "@mui/icons-material/Email";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useEffect, useMemo, useState } from "react";
import ContractMultistepForm from "../../components/ui/contractMultistepForm/contractMultistepForm.component";
import { SendReminder } from "../../components/settlements/sendReminder/sendReminder.component";
import { useHasPermission } from "../../contexts/auth";
import axios, { useApiRequest } from "../../api/axios";
import { useSnackbar } from "notistack";
import {
  ERROR_SNACKBAR,
  EXPIRED_CONTRACT_ACTIONS_ENDPOINT,
} from "../../consts";
import { CommonListResponse } from "../../types/crud";
import { ExpiredContractListItem } from "../../types/contracts";
import { ExpiredContractsActions } from "../../components/contracts/expiredContractsActions/expiredContractsActions.component";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";

export const ContractsActions = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRequest } = useApiRequest();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const endedParam = params.get("ended") || "";
  const [addContractOpened, setAddContractOpened] = useState<boolean>(false);
  const [sendReminderOpened, setSendReminderOpened] = useState<boolean>(false);
  const [expiredContractsOpened, setExpiredContractsOpened] =
    useState<boolean>(false);
  const [expiredContracts, setExpiredContracts] = useState<
    ExpiredContractListItem[]
  >([]);
  const canAddContract = useHasPermission("settlement.add_contract");
  const canChangeContract = useHasPermission("settlement.change_contract");
  const canChangeSettlement = useHasPermission("settlement.change_settlement");

  useEffect(() => {
    if (!canChangeContract) {
      return;
    }

    let mounted = true;

    const fetchExpiredContracts = async () => {
      const result = await executeRequest(() =>
        axios.get<CommonListResponse<ExpiredContractListItem>>(
          EXPIRED_CONTRACT_ACTIONS_ENDPOINT
        )
      );

      if (!mounted) {
        return;
      }

      if (!result?.success) {
        enqueueSnackbar(t("Failed to fetch expired contracts"), ERROR_SNACKBAR);
        return;
      }

      const fetchedContracts = result.data?.results || [];
      if (fetchedContracts.length === 0) {
        return;
      }

      setExpiredContracts(fetchedContracts);
      setExpiredContractsOpened(true);
    };

    fetchExpiredContracts();

    return () => {
      mounted = false;
    };
  }, [canChangeContract, enqueueSnackbar, executeRequest, t]);

  const actions: Action[] = [
    {
      id: "add-new-contract",
      icon: <AddIcon />,
      label: t("Add new contract"),
      onClick: () => {
        setAddContractOpened(true);
      },
      disabled: !canAddContract,
    },
    {
      icon: <EmailIcon />,
      label: t("Send reminder"),
      onClick: () => {
        setSendReminderOpened(true);
      },
      disabled: !canChangeSettlement,
    },
  ];

  const updateEndedParam = (value?: string) => {
    const nextParams = new URLSearchParams(location.search);

    if (value) {
      nextParams.set("ended", value);
    } else {
      nextParams.delete("ended");
    }

    const nextSearch = nextParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const quickFilterActions: Action[] = [
    {
      icon: <ListAltOutlinedIcon />,
      label: t("All contracts"),
      onClick: () => updateEndedParam(undefined),
      color: !endedParam ? "var(--primaryColor)" : undefined,
    },
    {
      icon: <HandshakeOutlinedIcon />,
      label: t("Active"),
      onClick: () => updateEndedParam("false"),
      color: endedParam === "false" ? "var(--primaryColor)" : undefined,
    },
    {
      icon: <EventBusyOutlinedIcon />,
      label: t("Ended"),
      onClick: () => updateEndedParam("true"),
      color: endedParam === "true" ? "var(--primaryColor)" : undefined,
    },
  ];

  const quickFilters = (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography
        component="legend"
        variant="subtitle2"
        sx={{
          px: 1,
          mb: 1,
        }}
      >
        {t("Quick filters")}
      </Typography>
      <Toolbar actions={quickFilterActions} noTopPadding showShortcut={false} />
    </Box>
  );

  const handleContractAdded = () => {
    setAddContractOpened(false);
    window.dispatchEvent(new Event("contractAdded"));
  };

  const handleExpiredContractsProcessed = () => {
    setExpiredContractsOpened(false);
    setExpiredContracts([]);
    window.dispatchEvent(new Event("contractAdded"));
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
        {quickFilters}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Toolbar actions={actions} noTopPadding />
        </Box>
      </Box>
      <CustomModal
        opened={addContractOpened}
        setOpened={setAddContractOpened}
        title={t("Add new contract")}
        content={
          <ContractMultistepForm onSuccess={handleContractAdded} />
        }
      />
      <CustomModal
        opened={sendReminderOpened}
        setOpened={setSendReminderOpened}
        title={t("Send reminder")}
        content={
          <SendReminder
            onSuccess={() => {
              setSendReminderOpened(false);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={expiredContractsOpened}
        setOpened={setExpiredContractsOpened}
        title={t("Expired contracts")}
        content={
          <ExpiredContractsActions
            contracts={expiredContracts}
            onSuccess={handleExpiredContractsProcessed}
          />
        }
      />
    </>
  );
};
